import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getClients, getInvoices, getInvoice, deleteInvoice, updateInvoice, updateClient, deleteClient } from '../services/api';
import { showSuccessToast, showErrorToast, showLoadingToast, updateLoadingToastSuccess, updateLoadingToastError } from '../utils/toastConfig';
import { FiUserPlus, FiFileText, FiUsers, FiLogOut, FiSearch, FiEdit2, FiTrash2, FiEye, FiX, FiChevronLeft, FiChevronRight, FiCalendar } from 'react-icons/fi';
import { MdCurrencyRupee } from "react-icons/md";
import CustomDatePicker from '../components/CustomDatePicker';
import './Dashboard.css';
import AddClient from './AddClient';

// Normalize invoice number: VIECPL/2026-27/0002 → VIECPL0002
const normalizeInvNum = (num) => {
  if (!num || !num.includes('/')) return num;
  const parts = num.split('/');
  return `VIECPL${parts[parts.length - 1]}`;
};

// Returns FY label like "2025-26" from an invoice date string
function getInvoiceFY(dateStr) {
  const d = new Date(dateStr);
  const year = d.getFullYear();
  const month = d.getMonth(); // 0-indexed; April = 3
  if (month >= 3) {
    return `${year}-${String(year + 1).slice(2)}`;
  } else {
    return `${year - 1}-${String(year).slice(2)}`;
  }
}

// SAC code per description
const SAC_CODES = {
  'Information Technology and Consultancy Services': '998313',
  'Education Consultancy Services': '999293',
};

// Sector → description (matches server-side DESCRIPTIONS)
const SECTOR_TO_DESCRIPTION = {
  'Information Technology': 'Information Technology and Consultancy Services',
  'Education': 'Education Consultancy Services',
};

// ── Invoice paper helpers ──────────────────────
const fmtInv = (v) => parseFloat(v || 0).toLocaleString('en-IN') + '/-';

const toWordsInv = (num) => {
  const a = ['','One','Two','Three','Four','Five','Six','Seven','Eight','Nine','Ten','Eleven','Twelve','Thirteen','Fourteen','Fifteen','Sixteen','Seventeen','Eighteen','Nineteen'];
  const b = ['','','Twenty','Thirty','Forty','Fifty','Sixty','Seventy','Eighty','Ninety'];
  if (num === 0) return 'Zero';
  const convert = (n) => {
    if (n < 20) return a[n];
    if (n < 100) return b[Math.floor(n/10)] + ' ' + a[n%10];
    if (n < 1000) return a[Math.floor(n/100)] + ' Hundred ' + convert(n%100);
    if (n < 100000) return convert(Math.floor(n/1000)) + ' Thousand ' + convert(n%1000);
    if (n < 10000000) return convert(Math.floor(n/100000)) + ' Lakh ' + convert(n%100000);
    return convert(Math.floor(n/10000000)) + ' Crore ' + convert(n%10000000);
  };
  return convert(Math.round(num)).replace(/\s+/g,' ').trim();
};
// ──────────────────────────────────────────────

const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
  'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
  'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Puducherry', 'Chandigarh',
  'Andaman and Nicobar Islands', 'Dadra and Nagar Haveli and Daman and Diu', 'Lakshadweep',
];

const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ clients: 0, invoices: 0, revenue: 0 });
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [clients, setClients] = useState([]);
  const invoicesPerPage = 8;
  const [showAddClientModal, setShowAddClientModal] = useState(false);
  const [showClients, setShowClients] = useState(false);

  // Invoice search & FY filter
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [selectedFY, setSelectedFY] = useState('All');

  // Delete modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Edit invoice modal
  const [showEditInvoiceModal, setShowEditInvoiceModal] = useState(false);
  const [editInvoice, setEditInvoice] = useState(null);
  const [editInvoiceForm, setEditInvoiceForm] = useState({
    invoice_date: '', client_id: '', amount: '', description: '', sac_code: '',
    gstn: '', pan_no: '', po_no: '', po_date: '',
    bank_name: '', bank_address: '', account_number: '', ifsc_code: '',
  });
  const [editingInvoice, setEditingInvoice] = useState(false);

  // Client search & edit
  const [clientSearch, setClientSearch] = useState('');
  const [editingClient, setEditingClient] = useState(null);
  const [editClientForm, setEditClientForm] = useState({ client_name: '', address: '', gst_number: '', state: '', sector: '' });
  const [savingClient, setSavingClient] = useState(false);

  // Delete client modal
  const [showDeleteClientModal, setShowDeleteClientModal] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [deletingClient, setDeletingClient] = useState(false);

  // Derive available FYs from loaded invoices (sorted newest first)
  const availableFYs = [...new Set(recentInvoices.map((inv) => getInvoiceFY(inv.invoice_date)))]
    .sort()
    .reverse();

  // Filter invoices by FY + search
  const filteredInvoices = recentInvoices.filter((inv) => {
    if (selectedFY !== 'All' && getInvoiceFY(inv.invoice_date) !== selectedFY) return false;
    if (!invoiceSearch.trim()) return true;
    const q = invoiceSearch.toLowerCase();
    return (
      inv.invoice_number?.toLowerCase().includes(q) ||
      inv.client_name?.toLowerCase().includes(q) ||
      new Date(inv.invoice_date).toLocaleDateString('en-IN').includes(q) ||
      parseFloat(inv.amount).toString().includes(q) ||
      (inv.tax_type === 'IGST' ? 'igst' : 'cgst+sgst').includes(q) ||
      parseFloat(inv.total_amount).toString().includes(q)
    );
  });

  const indexOfLastInvoice = currentPage * invoicesPerPage;
  const indexOfFirstInvoice = indexOfLastInvoice - invoicesPerPage;
  const currentInvoices = filteredInvoices.slice(indexOfFirstInvoice, indexOfLastInvoice);
  const totalPages = Math.ceil(filteredInvoices.length / invoicesPerPage);

  // Reset page when search or FY filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [invoiceSearch, selectedFY]);

  const goToNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };
  const goToPrevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const fetchDashboardData = async () => {
    try {
      const [clientsRes, invoicesRes] = await Promise.all([getClients(), getInvoices()]);
      setClients(clientsRes.data);
      const totalRevenue = invoicesRes.data.reduce((sum, inv) => sum + parseFloat(inv.total_amount), 0);
      setStats({ clients: clientsRes.data.length, invoices: invoicesRes.data.length, revenue: totalRevenue });
      setRecentInvoices(invoicesRes.data);
    } catch (err) {
      console.error("Dashboard fetch error:", err);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const handleLogout = () => {
    logout();
    showSuccessToast('Logged out successfully!');
    setTimeout(() => navigate('/login'), 1000);
  };

  // Delete invoice handlers
  const handleDeleteClick = (inv) => {
    setInvoiceToDelete(inv);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!invoiceToDelete) return;
    setDeleting(true);
    const toastId = showLoadingToast('Deleting invoice...');
    try {
      await deleteInvoice(invoiceToDelete.id);
      updateLoadingToastSuccess(toastId, 'Invoice deleted successfully!');
      setShowDeleteModal(false);
      setInvoiceToDelete(null);
      fetchDashboardData();
    } catch (err) {
      updateLoadingToastError(toastId, err.response?.data?.message || 'Failed to delete invoice');
    } finally {
      setDeleting(false);
    }
  };

  // Edit invoice handlers
  const handleEditInvoiceClick = async (inv) => {
    setEditInvoice(inv);
    const buildForm = (d) => ({
      invoice_date: d.invoice_date ? d.invoice_date.split('T')[0] : '',
      client_id: String(d.client_id || ''),
      amount: d.amount,
      description: d.description,
      sac_code: d.sac_code || SAC_CODES[d.description] || '998313',
      gstn:           d.gstn           || '36AAECV8279M1ZG',
      pan_no:         d.pan_no         || 'AAECV8279M',
      po_no:          d.po_no          || 'NIL',
      po_date:        d.po_date        || 'NIL',
      bank_name:      d.bank_name      || 'AXIS BANK',
      bank_address:   d.bank_address   || 'DD COLONY Hyderabad',
      account_number: d.account_number || '920020047084756',
      ifsc_code:      d.ifsc_code      || 'UTIB0004188',
    });
    setEditInvoiceForm(buildForm(inv));
    setShowEditInvoiceModal(true);
    // Fetch full invoice data (includes client_state, client_address, client_gst, sac_code)
    try {
      const { data } = await getInvoice(inv.id);
      setEditInvoice(data);
      setEditInvoiceForm(buildForm(data));
    } catch { /* keep using list data */ }
  };

  const handleEditInvoiceSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!editInvoiceForm.amount || parseFloat(editInvoiceForm.amount) <= 0) {
      showErrorToast('Please enter a valid amount');
      return;
    }
    setEditingInvoice(true);
    const toastId = showLoadingToast('Updating invoice...');
    try {
      await updateInvoice(editInvoice.id, editInvoiceForm);
      updateLoadingToastSuccess(toastId, 'Invoice updated successfully!');
      setShowEditInvoiceModal(false);
      setEditInvoice(null);
      fetchDashboardData();
    } catch (err) {
      updateLoadingToastError(toastId, err.response?.data?.message || 'Failed to update invoice');
    } finally {
      setEditingInvoice(false);
    }
  };

  // Client edit handlers
  const handleEditClientClick = (client) => {
    setEditingClient(client);
    setEditClientForm({
      client_name: client.client_name,
      address: client.address,
      gst_number: client.gst_number,
      state: client.state,
      sector: client.sector,
    });
  };

  const handleEditClientSubmit = async (e) => {
    e.preventDefault();
    if (!editClientForm.client_name || !editClientForm.address || !editClientForm.gst_number || !editClientForm.state || !editClientForm.sector) {
      showErrorToast('All fields are required');
      return;
    }
    setSavingClient(true);
    const toastId = showLoadingToast('Updating client...');
    try {
      await updateClient(editingClient.id, editClientForm);
      updateLoadingToastSuccess(toastId, 'Client updated successfully!');
      setEditingClient(null);
      fetchDashboardData();
    } catch (err) {
      updateLoadingToastError(toastId, err.response?.data?.message || 'Failed to update client');
    } finally {
      setSavingClient(false);
    }
  };

  // Delete client handler — opens modal
  const handleDeleteClient = (client) => {
    setClientToDelete(client);
    setShowDeleteClientModal(true);
  };

  const handleConfirmDeleteClient = async () => {
    if (!clientToDelete) return;
    setDeletingClient(true);
    const toastId = showLoadingToast('Deleting client...');
    try {
      await deleteClient(clientToDelete.id);
      updateLoadingToastSuccess(toastId, 'Client deleted successfully!');
      setShowDeleteClientModal(false);
      setClientToDelete(null);
      fetchDashboardData();
    } catch (err) {
      updateLoadingToastError(toastId, err.response?.data?.message || 'Failed to delete client');
    } finally {
      setDeletingClient(false);
    }
  };

  // Filter clients
  const filteredClients = clients.filter((c) => {
    if (!clientSearch.trim()) return true;
    return c.client_name.toLowerCase().includes(clientSearch.toLowerCase());
  });

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <div className="header-left">
          <img src="/veramasalogo.png" className="logo" alt="logo" />
        </div>
        <div className="header-right">
          <span className="welcome-text">Welcome, {user?.username}</span>
          <button className="btn-logout" onClick={handleLogout}>
            <FiLogOut /> Logout
          </button>
        </div>
      </header>

      <main className="dashboard-main">
        <div className="stats-row">
          <div className="stat-card" onClick={() => setShowClients(true)}>
            <div className="stat-icon-wrap"><FiUsers className="stat-icon" /></div>
            <div>
              <h3>{stats.clients}</h3>
              <p>Total Clients</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon-wrap"><FiFileText className="stat-icon" /></div>
            <div>
              <h3>{stats.invoices}</h3>
              <p>Total Invoices</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon-wrap"><MdCurrencyRupee className="stat-icon" /></div>
            <div>
              <h3>{stats.revenue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</h3>
              <p>Total Revenue</p>
            </div>
          </div>
        </div>

        <div className="action-buttons">
          <button className="btn-action btn-add-client" onClick={() => setShowAddClientModal(true)}>
            <FiUserPlus size={24} />
            <span>Add Client</span>
          </button>
          <button className="btn-action btn-gen-invoice" onClick={() => navigate('/generate-invoice')}>
            <FiFileText size={24} />
            <span>Generate Invoice</span>
          </button>
          <button className="btn-action btn-all-clients" onClick={() => setShowClients(true)}>
            <FiUsers size={24} />
            <span>All Clients</span>
          </button>
        </div>

        <div className="recent-section">
          {/* Section Header */}
          <div className="rs-header">
            <div className="rs-header-left">
              <div className="rs-title-row">
                <h2>Recent Invoices</h2>
                <span className="rs-count-badge">{filteredInvoices.length}</span>
              </div>
              <p className="rs-subtitle">Manage and track all your generated invoices</p>
            </div>
            <div className="rs-header-controls">
              {availableFYs.length > 0 && (
                <div className="rs-fy-wrap">
                  <FiCalendar size={13} className="rs-fy-icon" />
                  <select
                    className="rs-fy-select"
                    value={selectedFY}
                    onChange={(e) => setSelectedFY(e.target.value)}
                  >
                    <option value="All">All Years</option>
                    {availableFYs.map((fy) => (
                      <option key={fy} value={fy}>FY {fy}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="rs-search-wrap">
                <FiSearch className="rs-search-icon" />
                <input
                  type="text"
                  className="rs-search-input"
                  placeholder="Search invoices..."
                  value={invoiceSearch}
                  onChange={(e) => setInvoiceSearch(e.target.value)}
                />
                {invoiceSearch && (
                  <button className="rs-search-clear" onClick={() => setInvoiceSearch('')}><FiX size={14} /></button>
                )}
              </div>
            </div>
          </div>

          {filteredInvoices.length === 0 ? (
            <div className="rs-empty">
              <div className="rs-empty-icon"><FiFileText size={36} /></div>
              <h4>{invoiceSearch || selectedFY !== 'All' ? 'No results found' : 'No invoices yet'}</h4>
              <p>{invoiceSearch ? `No invoices match "${invoiceSearch}"` : selectedFY !== 'All' ? `No invoices found for FY ${selectedFY}` : 'Generate your first invoice to get started!'}</p>
            </div>
          ) : (
            <>
              {/* Table */}
              <div className="rs-table-container">
                <table className="rs-table">
                  <thead>
                    <tr>
                      <th>Invoice</th>
                      <th>Client</th>
                      <th>Date</th>
                      <th>Amount</th>
                      <th>Tax</th>
                      <th>Total</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentInvoices.map((inv, index) => (
                      <tr key={inv.id} className={index % 2 === 0 ? 'row-even' : 'row-odd'}>
                        <td>
                          <span className="inv-number">{normalizeInvNum(inv.invoice_number)}</span>
                        </td>
                        <td>
                          <span className="inv-client">{inv.client_name}</span>
                        </td>
                        <td>
                          <span className="inv-date">{new Date(inv.invoice_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                        </td>
                        <td>
                          <span className="inv-amount">&#8377;{parseFloat(inv.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                        </td>
                        <td>
                          <span className={`inv-tax-badge ${inv.tax_type === 'IGST' ? 'tax-igst' : 'tax-cgst'}`}>
                            {inv.tax_type === 'IGST' ? 'IGST' : 'CGST+SGST'}
                          </span>
                        </td>
                        <td>
                          <span className="inv-total">&#8377;{parseFloat(inv.total_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                        </td>
                        <td>
                          <div className="inv-actions">
                            <button className="inv-act-btn act-view" title="View Invoice" onClick={() => navigate(`/invoice/${inv.id}`)}>
                              <FiEye size={14} />
                            </button>
                            <button className="inv-act-btn act-edit" title="Edit Invoice" onClick={() => handleEditInvoiceClick(inv)}>
                              <FiEdit2 size={14} />
                            </button>
                            <button className="inv-act-btn act-delete" title="Delete Invoice" onClick={() => handleDeleteClick(inv)}>
                              <FiTrash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Footer: Results + Pagination */}
              <div className="rs-footer">
                <span className="rs-results-text">
                  Showing {indexOfFirstInvoice + 1}–{Math.min(indexOfLastInvoice, filteredInvoices.length)} of {filteredInvoices.length} invoices
                </span>
                {totalPages > 1 && (
                  <div className="rs-pagination">
                    <button className="rs-pg-btn" onClick={goToPrevPage} disabled={currentPage === 1}>
                      <FiChevronLeft size={16} />
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((num) => (
                      <button
                        key={num}
                        className={`rs-pg-num ${currentPage === num ? 'rs-pg-active' : ''}`}
                        onClick={() => setCurrentPage(num)}
                      >
                        {num}
                      </button>
                    ))}
                    <button className="rs-pg-btn" onClick={goToNextPage} disabled={currentPage === totalPages}>
                      <FiChevronRight size={16} />
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </main>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="dm-overlay" onClick={() => !deleting && setShowDeleteModal(false)}>
          <div className="dm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dm-icon">
              <FiTrash2 size={28} />
            </div>
            <h3>Delete Invoice</h3>
            <p>Are you sure you want to delete invoice <strong>{normalizeInvNum(invoiceToDelete?.invoice_number)}</strong>? This action cannot be undone.</p>
            <div className="dm-actions">
              <button className="dm-btn-cancel" onClick={() => setShowDeleteModal(false)} disabled={deleting}>Cancel</button>
              <button className="dm-btn-delete" onClick={handleConfirmDelete} disabled={deleting}>
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Invoice Modal — Full Invoice Paper */}
    {showEditInvoiceModal && editInvoice && (() => {
  const amt   = parseFloat(editInvoiceForm.amount) || 0;

  const selectedClient = clients.find(c => String(c.id) === String(editInvoiceForm.client_id));
  const clientState = selectedClient?.state || editInvoice.client_state || '';
  const same  = clientState.toLowerCase() === 'telangana';

  const cgst  = same ? amt * 0.09 : 0;
  const sgst  = same ? amt * 0.09 : 0;
  const igst  = !same ? amt * 0.18 : 0;
  const total = amt + (same ? cgst + sgst : igst);

  return (
    <div className="em-overlay" onClick={() => !editingInvoice && setShowEditInvoiceModal(false)}>
      <div className="em-invoice-wrapper" onClick={(e) => e.stopPropagation()}>

        {/* ACTION BAR */}
        <div className="em-invoice-bar">
          <div className="em-invoice-bar-info">
            <span>Edit Invoice</span>
            <strong>{normalizeInvNum(editInvoice.invoice_number)}</strong>
          </div>

          <div className="em-invoice-bar-btns">
            <button className="em-btn-cancel" onClick={() => setShowEditInvoiceModal(false)}>
              Cancel
            </button>
            <button className="em-btn-submit" onClick={handleEditInvoiceSubmit}>
              {editingInvoice ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>

        {/* PAPER */}
        <div className="em-paper-container">
          <div className="invoice-paper em-invoice-paper">

            {/* 🔥 OUTER BORDER WRAPPER (NEW) */}
            <div>

              {/* WATERMARK */}
              <div className="watermark">
                <img src="/v-logo.png" alt="watermark"/>
              </div>

              {/* HEADER */}
              <div className="header-box">
                <div className="header">
                  <img src="/v-logo.png" className="logo-left" alt="logo" />
                  <img src="/veramasa-text.png" className="logo-right" alt="logo" />
                </div>
              </div>

              {/* TITLE */}
              <div className="title">TAX INVOICE</div>

              <table className="invoice-table">
                <tbody>

                  <tr>
                    <td colSpan="2" rowSpan="6" className="company-box">
                      <b>VERAMASA IT AND EDUCATION CONSULTANCY PVT LTD.</b><br/>
                      <p style={{fontWeight:"lighter",marginTop:"7px"}}>
                        2-2-1130/8/1/A, Shivam Road, New Nallakunta,
                        Hyderabad, Telangana 500044
                      </p><br/>
                    </td>

                    <td className="label">Invoice No</td>
                    <td className="label">
                      {normalizeInvNum(editInvoice.invoice_number)}
                    </td>
                  </tr>

                  <tr>
                    <td className="label">SAC CODE</td>
                    <td className="label">
                      <input className="em-input-cell"
                        value={editInvoiceForm.sac_code}
                        onChange={(e)=>setEditInvoiceForm({...editInvoiceForm,sac_code:e.target.value})}
                      />
                    </td>
                  </tr>

                  <tr>
                    <td className="label">Date</td>
                    <td className="label">
                      <CustomDatePicker
                        value={editInvoiceForm.invoice_date}
                        onChange={(date)=>setEditInvoiceForm({...editInvoiceForm,invoice_date:date})}
                      />
                    </td>
                  </tr>

                  <tr>
                    <td className="label">GSTN</td>
                    <td className="label">
                      <input className="em-input-cell"
                        value={editInvoiceForm.gstn}
                        onChange={(e)=>setEditInvoiceForm({...editInvoiceForm,gstn:e.target.value})}
                      />
                    </td>
                  </tr>

                  <tr>
                    <td className="label">PAN No</td>
                    <td className="label">
                      <input className="em-input-cell"
                        value={editInvoiceForm.pan_no}
                        onChange={(e)=>setEditInvoiceForm({...editInvoiceForm,pan_no:e.target.value})}
                      />
                    </td>
                  </tr>

                  <tr>
                    <td className="label">PO NO</td>
                    <td className="label">
                      <input className="em-input-cell"
                        value={editInvoiceForm.po_no}
                        onChange={(e)=>setEditInvoiceForm({...editInvoiceForm,po_no:e.target.value})}
                      />
                    </td>
                  </tr>

                  <tr>
                    <td colSpan="2" className="center"><b>Customer</b></td>

                    <td className="label">PO DATE</td>
                    <td className="label">
                      <input className="em-input-cell"
                        value={editInvoiceForm.po_date}
                        onChange={(e)=>setEditInvoiceForm({...editInvoiceForm,po_date:e.target.value})}
                      />
                    </td>
                  </tr>

                  <tr>
                    <td colSpan="2" rowSpan="4" className="customer-box">

                      <select className="em-input-cell em-client-select"
                        value={editInvoiceForm.client_id}
                        onChange={(e)=>{
                          const cl = clients.find(c=>String(c.id)===e.target.value);
                          setEditInvoiceForm({...editInvoiceForm,client_id:e.target.value});
                        }}>
                        <option value="">— Select Client —</option>
                        {clients.map(c=>(
                          <option key={c.id} value={String(c.id)}>{c.client_name}</option>
                        ))}
                      </select>

                      <br/>

                      {selectedClient ? (
                        <>
                          {selectedClient.address}<br/>
                          <b style={{color:'red'}}>GSTIN {selectedClient.gst_number}</b>
                        </>
                      ) : (
                        <>
                          {editInvoice.client_address}<br/>
                          <b style={{color:'red'}}>GSTIN {editInvoice.client_gst}</b>
                        </>
                      )}
                    </td>

                    <td className="label">BANK</td>
                    <td className="label">
                      <input className="em-input-cell"
                        value={editInvoiceForm.bank_name}
                        onChange={(e)=>setEditInvoiceForm({...editInvoiceForm,bank_name:e.target.value})}
                      />
                    </td>
                  </tr>

                  <tr>
                    <td className="label">Address</td>
                    <td className="label">
                      <input className="em-input-cell"
                        value={editInvoiceForm.bank_address}
                        onChange={(e)=>setEditInvoiceForm({...editInvoiceForm,bank_address:e.target.value})}
                      />
                    </td>
                  </tr>

                  <tr>
                    <td className="label">Account Number</td>
                    <td className="label">
                      <input className="em-input-cell"
                        value={editInvoiceForm.account_number}
                        onChange={(e)=>setEditInvoiceForm({...editInvoiceForm,account_number:e.target.value})}
                      />
                    </td>
                  </tr>

                  <tr>
                    <td className="label">IFSC Code</td>
                    <td className="label">
                      <input className="em-input-cell"
                        value={editInvoiceForm.ifsc_code}
                        onChange={(e)=>setEditInvoiceForm({...editInvoiceForm,ifsc_code:e.target.value})}
                      />
                    </td>
                  </tr>

                  {/* SERVICE */}
                  <tr className="service-head">
                    <td>S.No</td>
                    <td>Description</td>
                    <td>Base Price</td>
                    <td>Total Value (INR)</td>
                  </tr>

                  <tr>
                    <td className="center">1</td>
                    <td>
                      <select className="em-input-cell"
                        value={editInvoiceForm.description}
                        onChange={(e)=>setEditInvoiceForm({...editInvoiceForm,description:e.target.value})}>
                        <option>Information Technology and Consultancy Services</option>
                        <option>Education Consultancy Services</option>
                      </select>
                    </td>
                    <td className="center">Base Price</td>
                    <td className="center">
                      <input type="number" className="em-input-cell"
                        value={editInvoiceForm.amount}
                        onChange={(e)=>setEditInvoiceForm({...editInvoiceForm,amount:e.target.value})}
                      />
                    </td>
                  </tr>

                  {/* TAX */}
                  <tr>
                    <td colSpan="3" style={{textAlign:'right'}}>CGST @ 9%</td>
                    <td style={{textAlign:'center'}}>{same ? fmtInv(cgst) : 'Nil'}</td>
                  </tr>

                  <tr>
                    <td colSpan="3" style={{textAlign:'right'}}>SGST @ 9%</td>
                    <td style={{textAlign:'center'}}>{same ? fmtInv(sgst) : 'Nil'}</td>
                  </tr>

                  <tr>
                    <td colSpan="3" style={{textAlign:'right'}}><b>IGST @ 18%</b></td>
                    <td style={{textAlign:'center'}}>{!same ? fmtInv(igst) : 'Nil'}</td>
                  </tr>

                  <tr>
                    <td colSpan="3" style={{textAlign:'right'}}>Grand Total</td>
                    <td style={{textAlign:'center'}}>{fmtInv(total)}</td>
                  </tr>

                  <tr>
                    <td colSpan="4" className="words">
                      Amount Chargeable ( in words ) :
                      <i> INR {toWordsInv(total)} Only</i>
                    </td>
                  </tr>

                </tbody>
              </table>

              {/* FOOTER */}
              <div className="invoice-footer">
                <img src="/seal.png" className="seal" alt="seal"/>

                <div className="sign-area">
                  <div>For VERAMASA IT AND EDUCATION<br/>CONSULTANCY Pvt Ltd.</div>
                  <img src="/sign.png" className="sign" alt="sign"/>
                  <div className="auth">Authorized Signature</div>
                </div>
              </div>

              {/* BOTTOM ADDRESS */}
              <div className="bottom-address">
                <b>Veramasa IT and Education Consultancy Pvt Ltd</b><br/>
                <p style={{fontSize:"12px"}}>
                  2-2-1130/8/1/A, Shivam Road, Hyderabad, Telangana 500044<br/>
                  website : www.veramasa.com | Email : info@veramasa.com
                </p>
              </div>

            </div>
          </div>
        </div>

      </div>
    </div>
  );
})()}

      {/* Add Client Modal */}
      {showAddClientModal && (
        <AddClient closeModal={() => setShowAddClientModal(false)} refreshClients={fetchDashboardData} />
      )}

      {/* All Clients Modal */}
      {showClients && (
        <div className="cm-overlay" onClick={() => { setShowClients(false); setEditingClient(null); setClientSearch(''); }}>
          <div className="cm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cm-header">
              <div>
                <h3>All Clients</h3>
                <p className="cm-header-sub">{filteredClients.length} client{filteredClients.length !== 1 ? 's' : ''} found</p>
              </div>
              <button className="cm-close" onClick={() => { setShowClients(false); setEditingClient(null); setClientSearch(''); }}><FiX size={18} /></button>
            </div>

            <div className="cm-search">
              <FiSearch className="cm-search-icon" />
              <input
                className="cm-search-input"
                type="text"
                placeholder="Search by client name..."
                value={clientSearch}
                onChange={(e) => setClientSearch(e.target.value)}
              />
              {clientSearch && (
                <button className="cm-search-clear" onClick={() => setClientSearch('')}><FiX size={12} /></button>
              )}
            </div>

            <div className="cm-list">
              {filteredClients.length === 0 ? (
                <div className="cm-empty">
                  <FiUsers size={28} />
                  <p>{clientSearch ? 'No clients match your search.' : 'No clients added yet.'}</p>
                </div>
              ) : (
                filteredClients.map((client) => (
                  <div key={client.id} className="cm-row">
                    {editingClient?.id === client.id ? (
                      <form className="cm-edit-form" onSubmit={handleEditClientSubmit}>
                        <div className="cm-edit-grid">
                          <div className="cm-edit-field">
                            <label>Client Name *</label>
                            <input type="text" value={editClientForm.client_name} onChange={(e) => setEditClientForm({ ...editClientForm, client_name: e.target.value })} required />
                          </div>
                          <div className="cm-edit-field">
                            <label>GST Number *</label>
                            <input type="text" value={editClientForm.gst_number} onChange={(e) => setEditClientForm({ ...editClientForm, gst_number: e.target.value })} maxLength={15} required />
                          </div>
                        </div>
                        <div className="cm-edit-field">
                          <label>Address *</label>
                          <textarea value={editClientForm.address} onChange={(e) => setEditClientForm({ ...editClientForm, address: e.target.value })} rows={2} required />
                        </div>
                        <div className="cm-edit-grid">
                          <div className="cm-edit-field">
                            <label>State *</label>
                            <select value={editClientForm.state} onChange={(e) => setEditClientForm({ ...editClientForm, state: e.target.value })} required>
                              <option value="">Select State</option>
                              {INDIAN_STATES.map((s) => (<option key={s} value={s}>{s}</option>))}
                            </select>
                          </div>
                          <div className="cm-edit-field">
                            <label>Sector *</label>
                            <select value={editClientForm.sector} onChange={(e) => setEditClientForm({ ...editClientForm, sector: e.target.value })} required>
                              <option value="">Select Sector</option>
                              <option value="Education">Education</option>
                              <option value="Information Technology">Information Technology</option>
                            </select>
                          </div>
                        </div>
                        <div className="cm-edit-actions">
                          <button type="button" className="cm-btn-cancel" onClick={() => setEditingClient(null)} disabled={savingClient}>Cancel</button>
                          <button type="submit" className="cm-btn-save" disabled={savingClient}>{savingClient ? 'Saving...' : 'Save'}</button>
                        </div>
                      </form>
                    ) : (
                      <div className="cm-row-content">
                        <div className="cm-row-avatar">
                          {client.client_name.charAt(0).toUpperCase()}
                        </div>
                        <div className="cm-row-info">
                          <h4>{client.client_name}</h4>
                          <p className="cm-row-addr">{client.address}</p>
                          <div className="cm-row-tags">
                            <span className="cm-tag">GST: {client.gst_number}</span>
                            <span className="cm-tag">{client.state}</span>
                            <span className="cm-tag">{client.sector}</span>
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                          <button className="cm-btn-edit" title="Edit" onClick={() => handleEditClientClick(client)}>
                            <FiEdit2 size={15} />
                          </button>
                          <button className="cm-btn-delete" title="Delete" onClick={() => handleDeleteClient(client)}>
                            <FiTrash2 size={15} />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Client Confirmation Modal — rendered last so it appears above All Clients */}
      {showDeleteClientModal && (
        <div className="dm-overlay dm-client-overlay" onClick={() => !deletingClient && setShowDeleteClientModal(false)}>
          <div className="dm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dm-icon">
              <FiTrash2 size={28} />
            </div>
            <h3>Delete Client</h3>
            <p>Are you sure you want to delete <strong>{clientToDelete?.client_name}</strong>? This action cannot be undone.</p>
            <div className="dm-actions">
              <button className="dm-btn-cancel" onClick={() => setShowDeleteClientModal(false)} disabled={deletingClient}>Cancel</button>
              <button className="dm-btn-delete" onClick={handleConfirmDeleteClient} disabled={deletingClient}>
                {deletingClient ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
